import { useState, useEffect, useMemo } from "react";
import { Radio, CheckCircle2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Brand colors for equipment folders
export const BRAND_COLORS = {
  "Sennheiser": "#1428A0",
  "Shure": "#D4AF37",
  "Anleon": "#34C759",
  "Relacart": "#FF6B00",
  "Audio-Technica": "#E31837",
  "Sony": "#000000",
  "AKG": "#FF0000",
  "Otros": "#71717A"
};

/**
 * Cascading Equipment Selector Component
 * Provides a 3-step selection: Brand → Model → Band
 */
export function CascadingEquipmentSelector({ 
  equipmentList, 
  selectedEquipmentId, 
  selectedBandId,
  onSelectEquipment, 
  onSelectBand,
  availableBands
}) {
  // Extract unique brands
  const brands = useMemo(() => {
    const brandSet = new Set(equipmentList.map(eq => eq.brand || "Otros"));
    return Array.from(brandSet).sort((a, b) => {
      if (a === "Otros") return 1;
      if (b === "Otros") return -1;
      return a.localeCompare(b);
    });
  }, [equipmentList]);

  // Get selected equipment details
  const selectedEquipment = useMemo(() => 
    equipmentList.find(eq => eq.id === selectedEquipmentId),
    [equipmentList, selectedEquipmentId]
  );

  // Currently selected brand (from selected equipment or empty)
  const [selectedBrand, setSelectedBrand] = useState(() => 
    selectedEquipment?.brand || ""
  );

  // Update brand when equipment changes externally
  useEffect(() => {
    if (selectedEquipment && selectedEquipment.brand !== selectedBrand) {
      setSelectedBrand(selectedEquipment.brand);
    }
  }, [selectedEquipment]);

  // Models filtered by selected brand - sorted by model name and grouped by similar frequencies
  const modelsInBrand = useMemo(() => {
    if (!selectedBrand) return [];
    return equipmentList
      .filter(eq => eq.brand === selectedBrand)
      .sort((a, b) => {
        // First sort by system_type (IEM first, then mic/microphone)
        const aIsMic = a.system_type === 'mic' || a.system_type === 'microphone';
        const bIsMic = b.system_type === 'mic' || b.system_type === 'microphone';
        if (aIsMic !== bIsMic) {
          return aIsMic ? 1 : -1; // IEM first
        }
        // Then sort by model name alphabetically
        return (a.model || '').localeCompare(b.model || '');
      });
  }, [equipmentList, selectedBrand]);

  // Handle brand change
  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    // Clear equipment selection when brand changes
    if (selectedEquipment?.brand !== brand) {
      onSelectEquipment("");
      onSelectBand("");
    }
  };

  // Handle model/equipment change
  const handleModelChange = (equipmentId) => {
    onSelectEquipment(equipmentId);
    // Reset band when model changes
    onSelectBand("");
  };

  const brandColor = BRAND_COLORS[selectedBrand] || BRAND_COLORS["Otros"];

  return (
    <div className="space-y-4">
      {/* Step 1: Brand Selection */}
      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-[#34C759]/20 text-[#34C759] text-xs flex items-center justify-center font-bold">1</span>
          Marca
        </Label>
        <Select value={selectedBrand} onValueChange={handleBrandChange}>
          <SelectTrigger 
            className="input-tactical" 
            data-testid="brand-select"
            style={{ borderColor: selectedBrand ? `${brandColor}50` : undefined }}
          >
            <SelectValue placeholder="Seleccionar marca..." />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            {brands.map(brand => {
              const color = BRAND_COLORS[brand] || BRAND_COLORS["Otros"];
              const count = equipmentList.filter(eq => eq.brand === brand).length;
              return (
                <SelectItem key={brand} value={brand}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: color }}
                    />
                    <span>{brand}</span>
                    <span className="text-xs text-[#71717A]">({count})</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Step 2: Model Selection */}
      <div className="form-group">
        <Label className="form-label flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
            selectedBrand ? "bg-[#34C759]/20 text-[#34C759]" : "bg-white/10 text-[#71717A]"
          }`}>2</span>
          Modelo
        </Label>
        <Select 
          value={selectedEquipmentId} 
          onValueChange={handleModelChange}
          disabled={!selectedBrand}
        >
          <SelectTrigger 
            className={`input-tactical ${!selectedBrand ? "opacity-50" : ""}`}
            data-testid="model-select"
          >
            <SelectValue placeholder={selectedBrand ? "Seleccionar modelo..." : "Primero selecciona una marca"} />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[350px]">
            {/* Group by system type */}
            {modelsInBrand.some(eq => eq.system_type === 'iem') && (
              <div className="px-2 py-1 text-xs font-semibold text-[#34C759] uppercase tracking-wider border-b border-white/5 mb-1">
                In-Ear Monitor (IEM)
              </div>
            )}
            {modelsInBrand
              .filter(eq => eq.system_type === 'iem')
              .map(eq => {
                const bands = eq.frequency_bands || {};
                const bandNames = Object.keys(bands);
                const bandRanges = bandNames.map(b => {
                  const band = bands[b];
                  const minF = band.min_freq || band.freq_min;
                  const maxF = band.max_freq || band.freq_max;
                  return minF && maxF ? `${minF}-${maxF}` : '';
                }).filter(Boolean).join(' | ');
                
                return (
                  <SelectItem key={eq.id} value={eq.id}>
                    <div className="flex flex-col py-0.5">
                      <span className="font-medium">{eq.model}</span>
                      <span className="text-xs text-[#71717A]">
                        {bandRanges ? `${bandNames.join(', ')} (${bandRanges} MHz)` : `Máx ${eq.max_systems || 8} sistemas`}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            
            {modelsInBrand.some(eq => eq.system_type === 'mic' || eq.system_type === 'microphone') && (
              <div className="px-2 py-1 text-xs font-semibold text-[#FFCC00] uppercase tracking-wider border-b border-white/5 mt-2 mb-1">
                Micrófono Inalámbrico
              </div>
            )}
            {modelsInBrand
              .filter(eq => eq.system_type === 'mic' || eq.system_type === 'microphone')
              .map(eq => {
                const bands = eq.frequency_bands || {};
                const bandNames = Object.keys(bands);
                const bandRanges = bandNames.map(b => {
                  const band = bands[b];
                  const minF = band.min_freq || band.freq_min;
                  const maxF = band.max_freq || band.freq_max;
                  return minF && maxF ? `${minF}-${maxF}` : '';
                }).filter(Boolean).join(' | ');
                
                return (
                  <SelectItem key={eq.id} value={eq.id}>
                    <div className="flex flex-col py-0.5">
                      <span className="font-medium">{eq.model}</span>
                      <span className="text-xs text-[#71717A]">
                        {bandRanges ? `${bandNames.join(', ')} (${bandRanges} MHz)` : `Máx ${eq.max_systems || 8} sistemas`}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            
            {/* Other equipment without specific system_type */}
            {modelsInBrand.some(eq => !eq.system_type || (eq.system_type !== 'iem' && eq.system_type !== 'mic' && eq.system_type !== 'microphone')) && (
              <div className="px-2 py-1 text-xs font-semibold text-[#A1A1AA] uppercase tracking-wider border-b border-white/5 mt-2 mb-1">
                Otros Equipos
              </div>
            )}
            {modelsInBrand
              .filter(eq => !eq.system_type || (eq.system_type !== 'iem' && eq.system_type !== 'mic' && eq.system_type !== 'microphone'))
              .map(eq => {
                const bands = eq.frequency_bands || {};
                const bandNames = Object.keys(bands);
                const bandRanges = bandNames.map(b => {
                  const band = bands[b];
                  const minF = band.min_freq || band.freq_min;
                  const maxF = band.max_freq || band.freq_max;
                  return minF && maxF ? `${minF}-${maxF}` : '';
                }).filter(Boolean).join(' | ');
                
                return (
                  <SelectItem key={eq.id} value={eq.id}>
                    <div className="flex flex-col py-0.5">
                      <span className="font-medium">{eq.model}</span>
                      <span className="text-xs text-[#71717A]">
                        {bandRanges ? `${bandNames.join(', ')} (${bandRanges} MHz)` : `Máx ${eq.max_systems || 8} sistemas`}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
      </div>

      {/* Step 3: Band Selection (only if equipment has multiple bands) */}
      {Object.keys(availableBands).length > 0 && (
        <div className="form-group">
          <Label className="form-label flex items-center gap-2">
            <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
              selectedEquipmentId ? "bg-[#34C759]/20 text-[#34C759]" : "bg-white/10 text-[#71717A]"
            }`}>3</span>
            Banda de Frecuencia
          </Label>
          <Select 
            value={selectedBandId} 
            onValueChange={onSelectBand}
            disabled={!selectedEquipmentId}
          >
            <SelectTrigger 
              className={`input-tactical ${!selectedEquipmentId ? "opacity-50" : ""}`}
              data-testid="band-select"
            >
              <SelectValue placeholder={selectedEquipmentId ? "Seleccionar banda..." : "Primero selecciona un modelo"} />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              {Object.entries(availableBands).map(([bandId, band]) => {
                const minF = band.min_freq || band.freq_min;
                const maxF = band.max_freq || band.freq_max;
                return (
                  <SelectItem key={bandId} value={bandId}>
                    <div className="flex flex-col">
                      <span className="font-medium">{band.name || bandId}</span>
                      <span className="text-xs text-[#71717A]">
                        {minF && maxF ? `${minF} - ${maxF} MHz` : 'Rango no especificado'}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Selection Summary */}
      {selectedEquipment && (
        <div className="mt-4 p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
            <span className="text-sm font-medium text-[#34C759]">Equipo Seleccionado</span>
          </div>
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-sm flex items-center justify-center"
              style={{ backgroundColor: `${brandColor}20` }}
            >
              <Radio className="w-4 h-4" style={{ color: brandColor }} />
            </div>
            <div>
              <p className="text-sm font-medium text-[#F4F4F5]">
                {selectedEquipment.full_name}
              </p>
              <p className="text-xs text-[#71717A]">
                {selectedBandId && availableBands[selectedBandId] 
                  ? `${availableBands[selectedBandId].name || selectedBandId} (${availableBands[selectedBandId].min_freq || availableBands[selectedBandId].freq_min}-${availableBands[selectedBandId].max_freq || availableBands[selectedBandId].freq_max} MHz)`
                  : `Máx ${selectedEquipment.max_systems || 8} sistemas`
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CascadingEquipmentSelector;
